"use strict";
/*

    AppCanvas
    - Manages frame loop
    - Manages root scene node and coordinate system
    - All coordinates are set in DOM pixel units relative to the canvas (unless marked as otherwise)
    
    - Should split up and move core parts to engine
*/
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    }
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
Object.defineProperty(exports, "__esModule", { value: true });
var GPUDevice_1 = require("engine/rendering/GPUDevice");
var Renderable_1 = require("engine/rendering/Renderable");
var Renderer_1 = require("engine/rendering/Renderer");
var RenderPass_1 = require("engine/rendering/RenderPass");
var SharedResources_1 = require("engine/SharedResources");
var InteractionEvent_1 = require("engine/ui/InteractionEvent");
var Object2D_1 = require("engine/ui/Object2D");
var React = require("react");
var ReactObject_1 = require("./ReactObject");
/**
 * AppCanvas
 * - root scene node and coordinate system
 * - entry point for canvas rendering
 * - emits user interaction events on scene nodes
 */
var AppCanvas = /** @class */ (function (_super) {
    __extends(AppCanvas, _super);
    function AppCanvas(props) {
        var _this = _super.call(this, props) || this;
        _this._reactObjects = new Array();
        _this.pointerEventSupport = 'PointerEvent' in window;
        _this.dragData = {};
        // we use the root node of the document to set the cursor style because it lets us maintain the cursor when dragging beyond the canvas
        _this.cursorTarget = window.document.documentElement;
        _this.activePointers = {};
        _this._cursorStyle = '';
        _this.onPointerEnter = function (e) {
            // enter and leave are special cases in that they don't directly translate into our InteractionEvents
            // we need a special system to handle these
            var interactionData = _this.interactionDataFromEvent(e);
            _this.activePointers[interactionData.pointerId] = {
                interactionData: interactionData,
                sourceEvent: e,
                lastHitNodes: new Set()
            };
            _this.handlePointerChanges();
        };
        _this.onPointerLeave = function (e) {
            var interactionData = _this.interactionDataFromEvent(e);
            delete _this.activePointers[interactionData.pointerId];
            _this.handlePointerChanges();
        };
        _this.onPointerMove = function (e) {
            _this.resetCursor();
            var interactionData = _this.interactionDataFromEvent(e);
            interactionData.buttonChange = -1; // normalize between MouseEvent and PointerEvent
            // update pointer data in activePointers
            if (e.target === _this.canvas) {
                // pointer moving directly over the canvas; ensure this pointer is considered an active pointer
                if (_this.activePointers[interactionData.pointerId] === undefined) {
                    _this.activePointers[interactionData.pointerId] = {
                        interactionData: interactionData,
                        sourceEvent: e,
                        lastHitNodes: new Set()
                    };
                }
                else {
                    _this.activePointers[interactionData.pointerId].interactionData = interactionData;
                }
            }
            else {
                // pointer not directly over the canvas another DOM element may be overlaying
                // this is no longer an active pointer
                delete _this.activePointers[interactionData.pointerId];
            }
            var dragData = _this.dragData[interactionData.pointerId];
            var defaultPrevented = false;
            if (dragData !== undefined) {
                defaultPrevented = defaultPrevented || _this.executePointerInteraction(dragData.inactiveNodes, 'dragstart', interactionData, function (init) {
                    dragData.activeNodes.push(init.target);
                    return new InteractionEvent_1.default(init, e);
                });
                dragData.inactiveNodes = [];
                defaultPrevented = defaultPrevented || _this.executePointerInteraction(dragData.activeNodes, 'dragmove', interactionData, function (init) {
                    return new InteractionEvent_1.default(init, e);
                });
            }
            if (e.target === _this.canvas) {
                if (!defaultPrevented) {
                    var eventName = 'pointermove';
                    var hitNodes = _this.hitTestNodesForInteraction([eventName], interactionData.worldX, interactionData.worldY);
                    _this.executePointerInteraction(hitNodes, eventName, interactionData, function (init) {
                        return new InteractionEvent_1.default(init, e);
                    });
                }
            }
            _this.applyCursor();
            _this.handlePointerChanges();
        };
        _this.onPointerDown = function (e) {
            _this.resetCursor();
            if (e.target === _this.canvas) {
                var eventName = 'pointerdown';
                var interactionData = _this.interactionDataFromEvent(e);
                // initialize drag data entry
                var dragData_1 = _this.dragData[interactionData.pointerId] = {
                    activeNodes: new Array(),
                    inactiveNodes: new Array(),
                    button: e.button,
                };
                // we need collect drag event nodes as well as pointerdown receiver to make sure drag events are emitted
                var hitNodes = _this.hitTestNodesForInteraction([eventName, 'dragstart', 'dragmove', 'dragend'], interactionData.worldX, interactionData.worldY).slice();
                _this.executePointerInteraction(hitNodes, eventName, interactionData, function (init) {
                    if (dragData_1.inactiveNodes.indexOf(init.target) === -1) {
                        dragData_1.inactiveNodes.push(init.target);
                    }
                    return new InteractionEvent_1.default(init, e);
                });
            }
            _this.applyCursor();
        };
        _this.onPointerUp = function (e) {
            _this.resetCursor();
            var interactionData = _this.interactionDataFromEvent(e);
            var dragData = _this.dragData[interactionData.pointerId];
            var defaultPrevented = false;
            if (dragData !== undefined && dragData.button === e.button) {
                // clear drag data entry
                delete _this.dragData[interactionData.pointerId];
                // fire 'dragend' on any nodes where drag was started
                defaultPrevented = _this.executePointerInteraction(dragData.activeNodes, 'dragend', interactionData, function (init) { return new InteractionEvent_1.default(init, e); }, false);
            }
            if (e.target === _this.canvas) {
                if (!defaultPrevented) {
                    var eventName = 'pointerup';
                    var hitNodes = _this.hitTestNodesForInteraction([eventName], interactionData.worldX, interactionData.worldY);
                    _this.executePointerInteraction(hitNodes, eventName, interactionData, function (init) { return new InteractionEvent_1.default(init, e); });
                }
            }
            _this.applyCursor();
        };
        _this.onClick = function (e) {
            if (e.target === _this.canvas) {
                var eventName = 'click';
                var interactionData = _this.interactionDataFromEvent(e);
                var hitNodes = _this.hitTestNodesForInteraction([eventName], interactionData.worldX, interactionData.worldY);
                _this.executePointerInteraction(hitNodes, eventName, interactionData, function (init) { return new InteractionEvent_1.default(init, e); });
            }
        };
        _this.onDoubleClick = function (e) {
            if (e.target === _this.canvas) {
                var eventName = 'dblclick';
                var interactionData = _this.interactionDataFromEvent(e);
                var hitNodes = _this.hitTestNodesForInteraction([eventName], interactionData.worldX, interactionData.worldY);
                _this.executePointerInteraction(hitNodes, eventName, interactionData, function (init) { return new InteractionEvent_1.default(init, e); });
            }
        };
        _this.onWheel = function (e) {
            if (e.target === _this.canvas) {
                var eventName = 'wheel';
                var interactionData = _this.interactionDataFromEvent(e);
                var hitNodes = _this.hitTestNodesForInteraction([eventName], interactionData.worldX, interactionData.worldY);
                _this.executePointerInteraction(hitNodes, eventName, interactionData, function (init) {
                    return new InteractionEvent_1.WheelInteractionEvent(__assign({}, init, { wheelDeltaMode: e.deltaMode, wheelDeltaX: e.deltaX, wheelDeltaY: e.deltaY, wheelDeltaZ: e.deltaZ }), e);
                });
            }
        };
        _this._hitNodes = new Array(); // micro-optimization: reuse array between events to prevent re-allocation
        _this.state = {
            reactObjects: []
        };
        _this.updateSceneContent();
        return _this;
    }
    AppCanvas.prototype.componentDidMount = function () {
        if (this.device != null) {
            console.error('Component mounted twice');
        }
        var glOptions = {
            antialias: true,
            stencil: true,
            alpha: false,
        };
        var gl = this.canvas.getContext('webgl', glOptions);
        if (gl == null) {
            throw 'WebGL not supported';
        }
        // @! temporary initial GL state for 2D drawing
        // in the future this should be applied to the root 2D node
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);
        gl.disable(gl.CULL_FACE);
        this.device = new GPUDevice_1.default(gl);
        this.renderer = new Renderer_1.default(this.device);
        this.addInputListeners();
        console.log("AppCanvas created with device %c\"" + this.device.name + "\"", 'font-weight: bold');
        function printExtensionSupport(name, enabled) {
            console.log("\t" + name + ": %c" + (enabled ? 'enabled' : 'disabled'), "font-weight: bold; color: " + (enabled ? 'green' : 'red'));
        }
        printExtensionSupport('Vertex Array Objects', this.device.capabilities.vertexArrayObjects);
        printExtensionSupport('Instancing', this.device.capabilities.instancing);
        printExtensionSupport('Float Textures', this.device.capabilities.floatTextures);
        printExtensionSupport('Float Textures Linear Filtering', this.device.capabilities.floatTexturesLinearFiltering);
        printExtensionSupport('Standard Derivatives', this.device.capabilities.standardDerivatives);
        if (!this.device.capabilities.instancing) {
            // support is expected on 100% of desktops and ~95% of mobile devices
            // a work around is possible to reach the final 5% of mobile devices
            throw 'WebGL extension ANGLE_instanced_arrays is required but not available on this device';
        }
    };
    AppCanvas.prototype.componentWillUnmount = function () {
        // for (let node of this.scene)
        this.scene.forEachSubNode(function (node) {
            if (node instanceof Renderable_1.default)
                node.releaseGPUResources();
        });
        SharedResources_1.default.release(this.device);
        this.device = null;
        this.renderer = null;
        this.removeInputListeners();
        if (this.props.onWillUnmount) {
            this.props.onWillUnmount();
        }
    };
    AppCanvas.prototype.componentDidUpdate = function (prevProps, prevState, snapshot) {
        if (prevProps.content != this.props.content) {
            this.updateSceneContent();
        }
        if (this.props.width !== prevProps.width ||
            this.props.height !== prevProps.height) {
            this.updateSceneTransform();
            this.scene.applyTransformToSubNodes();
            this.renderer.render(this.mainRenderPass);
        }
    };
    AppCanvas.prototype.render = function () {
        var _this = this;
        var pixelRatio = this.props.pixelRatio || window.devicePixelRatio || 1;
        var canvasWidth = this.props.width * pixelRatio + 'px';
        var canvasHeight = this.props.height * pixelRatio + 'px';
        var style = __assign({ position: 'relative', overflow: 'hidden', width: this.props.width + 'px', height: this.props.height + 'px' }, (this.props.style || {}));
        return (React.createElement("div", { className: ('app-canvas' + ((' ' + this.props.className) || '')).trimRight(), style: style },
            React.createElement("canvas", { ref: function (v) { return _this.canvas = v; }, width: canvasWidth, height: canvasHeight, style: __assign({ display: 'block', position: 'absolute', top: 0, left: 0, width: this.props.width + 'px', height: this.props.height + 'px', zIndex: 0 }, (this.props.canvasStyle || {})) }),
            this.state.reactObjects.map(function (ro) { return React.createElement(ReactObject_1.ReactObjectContainer, { key: ro.reactUid, reactObject: ro, scene: _this.scene }); }),
            this.props.children));
    };
    AppCanvas.prototype.renderCanvas = function () {
        this.renderer.render(this.mainRenderPass);
        this.updateReactObjects();
    };
    AppCanvas.prototype.handleUserInteraction = function () {
        this.handlePointerChanges();
    };
    AppCanvas.prototype.updateSceneContent = function () {
        this.scene = new Object2D_1.Object2D();
        if (this.props.content != null) {
            this.scene.add(this.props.content);
        }
        this.mainRenderPass = new RenderPass_1.default(null, this.scene, {
            clearColor: [1, 1, 1, 1],
            clearDepth: 1,
            clearStencil: 0x00
        });
        this.updateSceneTransform();
        this.scene.applyTransformToSubNodes();
    };
    /**
     * Apply DOM pixel coordinate system to the scene via a transform on the root node
     * - Flip z-axis from default OpenGL coordinates so that 1 = in front the screen and -1 is inside the screen
     * - z coordinates clip outside of -1 to 1
     * - (0, 0) corresponds to the top-left of the canvas
     * - (canvas.clientWidth, canvas.clientHeight) corresponds to the bottom left
     */
    AppCanvas.prototype.updateSceneTransform = function () {
        // width and height should be the _display_ size of the scene in DOM pixel units
        var w_dom = this.props.width;
        var h_dom = this.props.height;
        this.scene.x = -1;
        this.scene.y = 1;
        this.scene.z = 0;
        this.scene.sx = 2 / w_dom;
        this.scene.sy = -2 / h_dom;
        this.scene.sz = -1 * 1 / 5000;
        this.scene.w = w_dom;
        this.scene.h = h_dom;
    };
    /**
     * Given bounds in OpenGL display coordinates (clip-space), return the same bounds in DOM pixel coordinates (relative to the canvas)
     * This applies the inverse of the scene transform
     */
    AppCanvas.prototype.worldToCanvasSpaceBounds = function (worldSpaceBounds) {
        return {
            l: (worldSpaceBounds.l - this.scene.x) / this.scene.sx,
            r: (worldSpaceBounds.r - this.scene.x) / this.scene.sx,
            t: (worldSpaceBounds.t - this.scene.y) / this.scene.sy,
            b: (worldSpaceBounds.b - this.scene.y) / this.scene.sy,
        };
    };
    /**
     * Converts from canvas-space coordinates into clip-space, which is the world-space of Object2D nodes
     */
    AppCanvas.prototype.canvasToWorldSpacePosition = function (canvasSpacePosition) {
        return {
            x: (canvasSpacePosition.x / this.props.width) * 2 - 1,
            y: -((canvasSpacePosition.y / this.props.height) * 2 - 1),
        };
    };
    AppCanvas.prototype.updateReactObjects = function () {
        var _this = this;
        // find all react nodes in the scene
        var reactObjectIndex = 0;
        var reactObjectsChanged = false;
        // for (let node of this.scene)
        this.scene.forEachSubNode(function (node) {
            if (node instanceof ReactObject_1.ReactObject) {
                var last = _this._reactObjects[reactObjectIndex];
                if (!reactObjectsChanged) {
                    reactObjectsChanged = last !== node;
                }
                _this._reactObjects[reactObjectIndex] = node;
                reactObjectIndex++;
            }
        });
        reactObjectsChanged = reactObjectsChanged || (reactObjectIndex !== this._reactObjects.length);
        // trim excess nodes from the previous frame
        if (reactObjectIndex < this._reactObjects.length) {
            this._reactObjects.length = reactObjectIndex;
        }
        // trigger react re-render of viewer if the node list has changed
        if (reactObjectsChanged) {
            this.setState({
                reactObjects: this._reactObjects
            });
        }
    };
    /**
     * Returns the event position relative to the canvas
     */
    AppCanvas.prototype.mouseEventToCanvasSpacePosition = function (e) {
        var x = 0;
        var y = 0;
        var canvasRect = this.canvas.getBoundingClientRect();
        // canvas coordinate in absolute space, rather than viewport space
        // this means that the coordinates are the same independent of page scroll
        // see https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect
        var canvasX = window.scrollX + canvasRect.left;
        var canvasY = window.scrollY + canvasRect.top;
        x = e.pageX - canvasX;
        y = e.pageY - canvasY;
        return {
            x: x,
            y: y,
        };
    };
    AppCanvas.prototype.addInputListeners = function () {
        if (this.pointerEventSupport) {
            this.canvas.addEventListener('pointerdown', this.onPointerDown);
            window.addEventListener('pointerup', this.onPointerUp);
            window.addEventListener('pointermove', this.onPointerMove);
            window.addEventListener('pointerenter', this.onPointerEnter);
            window.addEventListener('pointerleave', this.onPointerLeave);
        }
        else {
            this.canvas.addEventListener('mousedown', this.onPointerDown);
            window.addEventListener('mouseup', this.onPointerUp);
            window.addEventListener('mousemove', this.onPointerMove);
            window.addEventListener('mouseenter', this.onPointerEnter);
            window.addEventListener('mouseleave', this.onPointerLeave);
        }
        this.canvas.addEventListener('click', this.onClick);
        this.canvas.addEventListener('dblclick', this.onDoubleClick);
        this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    };
    AppCanvas.prototype.removeInputListeners = function () {
        if (this.pointerEventSupport) {
            this.canvas.removeEventListener('pointerdown', this.onPointerDown);
            window.removeEventListener('pointerup', this.onPointerUp);
            window.removeEventListener('pointermove', this.onPointerMove);
            window.removeEventListener('pointerenter', this.onPointerEnter);
            window.removeEventListener('pointerleave', this.onPointerLeave);
        }
        else {
            this.canvas.removeEventListener('mousedown', this.onPointerDown);
            window.removeEventListener('mouseup', this.onPointerUp);
            window.removeEventListener('mousemove', this.onPointerMove);
            window.removeEventListener('mouseenter', this.onPointerEnter);
            window.removeEventListener('mouseleave', this.onPointerLeave);
        }
        this.canvas.removeEventListener('click', this.onClick);
        this.canvas.removeEventListener('dblclick', this.onDoubleClick);
        this.canvas.removeEventListener('wheel', this.onWheel, { passive: false });
    };
    AppCanvas.prototype.resetCursor = function () {
        this._cursorStyle = null;
    };
    AppCanvas.prototype.applyCursor = function () {
        var newStyle = this._cursorStyle === null ? '' : this._cursorStyle;
        if (this.cursorTarget.style.cursor !== newStyle) {
            this.cursorTarget.style.setProperty('cursor', newStyle, 'important');
        }
    };
    AppCanvas.prototype.handlePointerChanges = function () {
        var _loop_1 = function (pointerId) {
            var inactivePointer = this_1._lastActivePointers[pointerId];
            if (this_1.activePointers[pointerId] === undefined) {
                // pointer became inactive, fire 'pointerleave' on all nodes it was hitting 
                this_1.executePointerInteraction(inactivePointer.lastHitNodes, 'pointerleave', inactivePointer.interactionData, function (init) { return new InteractionEvent_1.default(init, inactivePointer.sourceEvent); });
            }
        };
        var this_1 = this;
        // fire pointerleave for any pointers that are no longer active in this frame
        for (var pointerId in this._lastActivePointers) {
            _loop_1(pointerId);
        }
        // reset lastActivePointers and fill in from active pointers
        this._lastActivePointers = {};
        var _loop_2 = function (pointerId) {
            var e_1, _a, e_2, _b;
            var activePointer = this_2.activePointers[pointerId];
            var interactionData = activePointer.interactionData, lastHitNodes = activePointer.lastHitNodes, sourceEvent = activePointer.sourceEvent;
            var hitNodes = this_2.hitTestNodesForInteraction([
                'pointerenter',
                'pointerleave'
            ], interactionData.worldX, interactionData.worldY);
            this_2._lastActivePointers[pointerId] = activePointer;
            // early exit
            if (hitNodes.length === 0 && lastHitNodes.size === 0) {
                return "continue";
            }
            // find delta since last call
            var addedNodes = new Set();
            var removedNodes = new Set();
            try {
                for (var hitNodes_1 = __values(hitNodes), hitNodes_1_1 = hitNodes_1.next(); !hitNodes_1_1.done; hitNodes_1_1 = hitNodes_1.next()) {
                    var node = hitNodes_1_1.value;
                    if (!lastHitNodes.has(node)) {
                        addedNodes.add(node);
                        lastHitNodes.add(node);
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (hitNodes_1_1 && !hitNodes_1_1.done && (_a = hitNodes_1.return)) _a.call(hitNodes_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
            try {
                for (var lastHitNodes_1 = __values(lastHitNodes), lastHitNodes_1_1 = lastHitNodes_1.next(); !lastHitNodes_1_1.done; lastHitNodes_1_1 = lastHitNodes_1.next()) {
                    var node = lastHitNodes_1_1.value;
                    if (hitNodes.indexOf(node) === -1) {
                        removedNodes.add(node);
                        lastHitNodes.delete(node);
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (lastHitNodes_1_1 && !lastHitNodes_1_1.done && (_b = lastHitNodes_1.return)) _b.call(lastHitNodes_1);
                }
                finally { if (e_2) throw e_2.error; }
            }
            var totalNodeChange = addedNodes.size + removedNodes.size;
            // early exit
            if (totalNodeChange === 0)
                return "continue";
            this_2.executePointerInteraction(addedNodes, 'pointerenter', interactionData, function (init) { return new InteractionEvent_1.default(init, sourceEvent); });
            this_2.executePointerInteraction(removedNodes, 'pointerleave', interactionData, function (init) { return new InteractionEvent_1.default(init, sourceEvent); });
        };
        var this_2 = this;
        // for all registered pointers:
        for (var pointerId in this.activePointers) {
            _loop_2(pointerId);
        }
    };
    AppCanvas.prototype.hitTestNodesForInteraction = function (interactionEventNames, worldX, worldY) {
        var hitNodeIndex = 0;
        var hitNodes = this._hitNodes;
        this.scene.forEachSubNode(function (node) {
            var e_3, _a;
            if (node instanceof Object2D_1.Object2D) {
                var nodeInternal = node;
                // we can skip this node if we know it doesn't have any interaction behaviors
                var listeners = 0;
                try {
                    for (var interactionEventNames_1 = __values(interactionEventNames), interactionEventNames_1_1 = interactionEventNames_1.next(); !interactionEventNames_1_1.done; interactionEventNames_1_1 = interactionEventNames_1.next()) {
                        var name_1 = interactionEventNames_1_1.value;
                        listeners += nodeInternal.interactionEventListenerCount[name_1];
                    }
                }
                catch (e_3_1) { e_3 = { error: e_3_1 }; }
                finally {
                    try {
                        if (interactionEventNames_1_1 && !interactionEventNames_1_1.done && (_a = interactionEventNames_1.return)) _a.call(interactionEventNames_1);
                    }
                    finally { if (e_3) throw e_3.error; }
                }
                if (node.cursorStyle == null &&
                    listeners <= 0)
                    return;
                var worldSpaceBounds = node.getWorldBounds();
                // hit-test position with object bounds
                if (worldX >= worldSpaceBounds.l &&
                    worldX <= worldSpaceBounds.r &&
                    worldY >= worldSpaceBounds.b &&
                    worldY <= worldSpaceBounds.t) {
                    hitNodes[hitNodeIndex++] = node;
                }
            }
        });
        // trim excess elements from last use
        if (hitNodeIndex < hitNodes.length) {
            hitNodes.length = hitNodeIndex;
        }
        // top-most nodes should receive events first
        hitNodes.sort(this.compareZ);
        return hitNodes;
    };
    AppCanvas.prototype.executePointerInteraction = function (nodes, interactionEventName, interactionData, constructEvent, setCursor) {
        if (setCursor === void 0) { setCursor = true; }
        var e_4, _a;
        var defaultPrevented = false;
        try {
            for (var nodes_1 = __values(nodes), nodes_1_1 = nodes_1.next(); !nodes_1_1.done; nodes_1_1 = nodes_1.next()) {
                var node = nodes_1_1.value;
                var nodeInternal = node;
                var worldSpaceBounds = node.getWorldBounds();
                var fx = (interactionData.worldX - worldSpaceBounds.l) / (worldSpaceBounds.r - worldSpaceBounds.l);
                var fy = (interactionData.worldY - worldSpaceBounds.t) / (worldSpaceBounds.b - worldSpaceBounds.t);
                // populate node-specific event fields
                interactionData.target = node;
                interactionData.localX = fx * nodeInternal.computedWidth;
                interactionData.localY = fy * nodeInternal.computedHeight;
                interactionData.fractionX = fx;
                interactionData.fractionY = fy;
                var eventObject = constructEvent(interactionData);
                var eventObjectInternal = eventObject;
                // trigger event on node
                nodeInternal.eventEmitter.emit(interactionEventName, eventObject);
                // update cursor style
                if (setCursor && this._cursorStyle == null && node.cursorStyle != null) {
                    this._cursorStyle = node.cursorStyle;
                }
                defaultPrevented = eventObjectInternal.defaultPrevented || defaultPrevented;
                // if user has executed stopPropagation() then do not emit on subsequent nodes
                if (eventObjectInternal.propagationStopped)
                    break;
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (nodes_1_1 && !nodes_1_1.done && (_a = nodes_1.return)) _a.call(nodes_1);
            }
            finally { if (e_4) throw e_4.error; }
        }
        return defaultPrevented;
    };
    AppCanvas.prototype.interactionDataFromEvent = function (e) {
        var canvasSpacePosition = this.mouseEventToCanvasSpacePosition(e);
        var worldSpacePosition = this.canvasToWorldSpacePosition(canvasSpacePosition);
        var interactionData = {
            buttonChange: e.button,
            buttonState: e.buttons,
            altKey: e.altKey,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey,
            metaKey: e.metaKey,
            canvasX: canvasSpacePosition.x,
            canvasY: canvasSpacePosition.y,
            worldX: worldSpacePosition.x,
            worldY: worldSpacePosition.y,
            // PointerEvent data, defaults to mouse events
            pointerId: -1,
            pointerType: 'mouse',
            isPrimary: true,
            width: 1,
            height: 1,
            pressure: 0,
            tiltX: 0,
            tiltY: 0,
            // node-specific
            target: null,
            localX: 0,
            localY: 0,
            fractionX: 0,
            fractionY: 0,
        };
        // set pointer event data if it's available
        if (this.pointerEventSupport && e instanceof PointerEvent) {
            interactionData.pointerId = e.pointerId;
            interactionData.pointerType = e.pointerType;
            interactionData.isPrimary = e.isPrimary;
            interactionData.width = e.width;
            interactionData.height = e.height;
            interactionData.pressure = e.pressure;
            interactionData.tiltX = e.tiltX;
            interactionData.tiltY = e.tiltY;
        }
        return interactionData;
    };
    AppCanvas.prototype.compareZ = function (a, b) {
        return a.getWorldZ() - b.getWorldZ();
    };
    return AppCanvas;
}(React.Component));
exports.AppCanvas = AppCanvas;
exports.default = AppCanvas;
